// OtpVerification.js — corrected
import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  FetchFromServer,
  InsertToServer,
  UpdateToServer,
} from "../api/ServerApi";
import { HOME_ROUTE, LOGIN_ROUTE } from "../appMode";
import { openOtpWhatsApp, OTP_SENDER_WHATSAPP } from "../utils/mobileNumber";




const OtpVerification = () => {
  const [otp, setOtp] = useState("");
  const navigate = useNavigate();
  const location = useLocation(); // ✅ moved inside component

  useEffect(() => {
    // Only auto-redirect to the active home route when OTP is verified AND
    // we're NOT currently on /smartsearch (prevents redirect when visiting /smartsearch).
    if (
      localStorage.getItem("OtpVerified") === "true" &&
      location.pathname !== "/smartsearch"
    ) {
      navigate(HOME_ROUTE, { replace: true });
    }
  }, [navigate, location.pathname]);

 const handleVerifyOtp = async () => {
  const stored = localStorage.getItem("Otp")?.trim();
  const entered = otp.trim();

  if (!entered) {
    alert("Enter OTP");
    return;
  }

  if (entered !== stored) {
    alert("Wrong OTP!");
    return;
  }

  const deviceId = localStorage.getItem("DEVICE_ID");

  // ------------------------------------------------------------------
  // PASSWORD-RESET PATH (existing user)
  // The number was already registered, so the OTP proves ownership and the
  // password they typed replaces the old one. Nothing is created — that is
  // what stops a duplicate account — and they are signed in afterwards so the
  // reset and the login are one step.
  // ------------------------------------------------------------------
  if (localStorage.getItem("OtpMode") === "reset") {
    const mobile = localStorage.getItem("OtpMobile");
    const newPassword = localStorage.getItem("NewPassword");

    if (!/^\d{4}$/.test(String(newPassword || ""))) {
      alert("The new password is missing. Please start again.");
      navigate("/signup", { replace: true });
      return;
    }

    try {
      // GetUserDetailsbyMobileNumber is only on the open /api/ route.
      const rows = await FetchFromServer(
        "GetUserDetailsbyMobileNumber",
        { MobileNumber: Number(mobile) },
        { plain: true, open: true }
      );

      if (!Array.isArray(rows) || rows.length === 0) {
        alert("That account could not be found. Please sign up again.");
        navigate("/signup", { replace: true });
        return;
      }

      const user = rows[0];

      // Apply the new password. This is the whole point of the flow, so a
      // failure here must NOT fall through to signing them in — they would
      // think the reset worked and be locked out next time.
      const updateResult = await UpdateToServer("UpdateUsers", {
        data: { Password: newPassword },
        where: `UserId = ${Number(user.UserId)}`,
      });

      const res = Array.isArray(updateResult) ? updateResult[0] : updateResult;
      const updated =
        res?.status === "Success" ||
        res?.status === true ||
        Number(res?.affectedRows) > 0;

      if (!updated) {
        console.error("Password update failed", updateResult);
        alert("Could not update the password. Please try again.");
        return;
      }

      if (deviceId) {
        try {
          await UpdateToServer("UpdateUsers", {
            data: { ReactDeviceId: deviceId },
            where: `UserId = ${Number(user.UserId)}`,
          });
        } catch (err) {
          console.error("Device binding failed", err);
          // do NOT block sign-in on this
        }
      }

      localStorage.setItem("UserId", user.UserId);
      localStorage.setItem("MobileNumber", user.MobileNumber);
      localStorage.setItem("Name", user.Name ?? "");
      localStorage.setItem("Surname", user.Surname ?? "");
      localStorage.setItem("SuperAdmin", user.SuperAdmin ?? 0);
      localStorage.setItem("OtpVerified", "true");

      localStorage.removeItem("Otp");
      localStorage.removeItem("OtpMode");
      localStorage.removeItem("OtpMobile");
      localStorage.removeItem("NewPassword");
      localStorage.removeItem("FromSignup");
      localStorage.removeItem("SignupPayload");

      alert("Password updated. Signing you in.");
      navigate(HOME_ROUTE, { replace: true });
      return;
    } catch (err) {
      console.error("Password reset failed", err);
      alert("Could not reset the password. Please try again.");
      return;
    }
  }

  // ------------------------------------------------------------------
  // SIGN-UP PATH
  // The account is created here, not in SmartifySignup — the signup screen
  // only sends the OTP and stashes the form. Previously this function read
  // TempUserId / TempMobile / TempPassword, which nothing ever wrote, so no
  // user was created and the app was entered with UserId = 0.
  // ------------------------------------------------------------------
  if (localStorage.getItem("FromSignup") === "true") {
    let payload = null;
    try {
      payload = JSON.parse(localStorage.getItem("SignupPayload") || "null");
    } catch (err) {
      payload = null;
    }

    if (!payload || !payload.MobileNumber || !payload.Password) {
      alert("Signup details are missing. Please sign up again.");
      navigate("/signup", { replace: true });
      return;
    }

    try {
      // AddUsers is only exposed on the open /api/ route (it has no
      // /apisecure/ counterpart), so address it by full URL.
      await InsertToServer("AddUsers", {
        Surname: payload.Surname,
        Name: payload.Name,
        FatherHusband: payload.FatherHusband,
        EmailId: payload.EmailId,
        MobileNumber: Number(payload.MobileNumber),
        Password: payload.Password,
      });

      // Read the account back to get the server-assigned UserId.
      const created = await FetchFromServer(
        "GetUserDetailsForCorporationbyMobileNumberAndPassword",
        {
          MobileNumber: Number(payload.MobileNumber),
          Password: payload.Password,
        }
      );

      if (!Array.isArray(created) || created.length === 0) {
        alert("Account was created but could not be loaded. Please log in.");
        navigate(LOGIN_ROUTE, { replace: true });
        return;
      }

      const user = created[0];

      if (deviceId) {
        try {
          await UpdateToServer("UpdateUsers", {
            data: { ReactDeviceId: deviceId },
            where: `UserId = ${Number(user.UserId)}`,
          });
        } catch (err) {
          console.error("Device binding failed", err);
          // do NOT block signup on this
        }
      }

      localStorage.setItem("UserId", user.UserId);
      localStorage.setItem("MobileNumber", user.MobileNumber);
      localStorage.setItem("Name", user.Name ?? payload.Name);
      localStorage.setItem("Surname", user.Surname ?? payload.Surname);
      localStorage.setItem("SuperAdmin", user.SuperAdmin ?? 0);
      localStorage.setItem("OtpVerified", "true");

      localStorage.removeItem("SignupPayload");
      localStorage.removeItem("FromSignup");
      localStorage.removeItem("Otp");
      localStorage.removeItem("OtpMode");
      localStorage.removeItem("OtpMobile");
      localStorage.removeItem("NewPassword");

      alert("Account created!");
      navigate(HOME_ROUTE, { replace: true });
      return;
    } catch (err) {
      console.error("Signup failed", err);
      alert("Could not create the account. Please try again.");
      return;
    }
  }

  // ------------------------------------------------------------------
  // RE-VERIFICATION PATH (existing user, Temp* values set by the caller)
  // ------------------------------------------------------------------
  const userId = Number(localStorage.getItem("TempUserId"));
  const mobile = localStorage.getItem("TempMobile");

  if (!userId || !mobile) {
    alert("Session details are missing. Please log in again.");
    navigate(LOGIN_ROUTE, { replace: true });
    return;
  }

  try {
    if (deviceId) {
      await UpdateToServer("UpdateUsers", {
        data: {
          ReactDeviceId: deviceId,
        },
        where: `UserId = ${userId}`,
      });
    }
  } catch (err) {
    console.error("UpdateUsers failed", err);
    // ❗ do NOT block login if this fails
  }

  // ✅ Save final login details
  localStorage.setItem("UserId", userId);
  localStorage.setItem("MobileNumber", mobile);
  localStorage.setItem("OtpVerified", "true");

  // cleanup temp values
  localStorage.removeItem("TempUserId");
  localStorage.removeItem("TempMobile");
  localStorage.removeItem("TempPassword");

  alert("OTP Verified!");
  navigate(HOME_ROUTE, { replace: true });
};


  return (
    <div style={styles.body}>
      <div style={styles.container}>
        <div style={styles.title}>Verify OTP</div>
        <div style={styles.loginBox}>
          <input
            type="text"
            placeholder="Enter OTP"
            value={otp}
            onChange={(e) => setOtp(e.target.value)}
            style={styles.input}
          />
          <button
            onClick={handleVerifyOtp}
            style={{ ...styles.button, ...styles.verify }}
          >
            Verify
          </button>

          {/* Same shortcut as the signup screen — the code arrives on WhatsApp,
              so make it one tap to go and read it. */}
          <button
            type="button"
            onClick={openOtpWhatsApp}
            style={{ ...styles.button, ...styles.whatsapp }}
          >
            Open WhatsApp to read the OTP
          </button>

          <div style={styles.note}>
            {localStorage.getItem("OtpMobile")
              ? `Sent to ${localStorage.getItem("OtpMobile")} from +${OTP_SENDER_WHATSAPP}`
              : `The code arrives from +${OTP_SENDER_WHATSAPP}`}
          </div>
        </div>
      </div>
    </div>
  );
};

const styles = {
  body: {
    backgroundImage:
      'url("https://wallpapers.com/images/hd/usa-chicago-riverwalk-at-night-1jaq0bwc5dc0g6n0.jpg")',
    backgroundSize: "cover",
    minHeight: "100vh",
    overflowY: "auto",
  },
  container: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "100vh",
    padding: "24px 12px",
    boxSizing: "border-box",
    flexDirection: "column",
  },
  title: {
    fontSize: "48px",
    color: "white",
    fontWeight: "bold",
    marginBottom: "20px",
  },
  loginBox: {
    background: "rgba(255,255,255,0.1)",
    padding: "20px",
    width: "340px",
    borderRadius: "8px",
  },
  input: {
    width: "100%",
    padding: "14px",
    margin: "10px 0",
    borderRadius: "6px",
  },
  button: {
    width: "100%",
    padding: "14px",
    borderRadius: "6px",
    cursor: "pointer",
  },
  verify: { background: "#1877f2", color: "white" },
  whatsapp: { background: "#25D366", color: "white", marginTop: 10 },
  note: {
    color: "#e9ecef",
    fontSize: 12,
    textAlign: "center",
    marginTop: 10,
    lineHeight: 1.4,
  },
};

export default OtpVerification;
